import * as Sentry from '@sentry/react';

/**
 * A failed `fetch` (device offline, dropped wifi, DNS, request aborted)
 * rejects with a status-less error: `TypeError: Load failed` on Safari,
 * "Failed to fetch" on Chrome, "NetworkError" on Firefox. On flaky
 * patient phones and kiosks these happen constantly and self-heal on the
 * next attempt, so reporting each one floods Sentry with zero user impact
 * (HEALTHCOM-SCHEDULE-23 / -25). A genuine HTTP error always carries a
 * status — from Amplify directly (`err.status`) or via the ChiroUpAPI /
 * Safari16API wrapper (`err.response.data.statusCode`) — so we only treat
 * rejections with NO status as transient.
 */
export const isTransientNetworkError = (err: any): boolean => {
  if (
    err?.status != null ||
    err?.response?.status != null ||
    err?.response?.data?.statusCode != null
  ) {
    return false;
  }

  const msg = String(err?.message ?? err ?? '');

  return /load failed|failed to fetch|networkerror|network request failed|the network connection was lost|the internet connection appears to be offline|cancell?ed/i.test(
    msg
  );
};

/**
 * A submit whose survey/consent was already completed — the patient finished it
 * in another tab or an earlier session, or a double-submit/retry race — comes
 * back as a 400 with an "already been completed" message. Like transient network
 * errors, this is expected and self-resolving (the work is already done), so it
 * is not a real failure worth reporting (HEALTHCOM-SCHEDULE-27 / -2A / -29).
 */
export const isAlreadyCompletedError = (err: any): boolean => {
  const statusCode = err?.response?.data?.statusCode ?? err?.status;

  if (statusCode !== 400) {
    return false;
  }

  const msg = String(err?.response?.data?.message ?? err?.message ?? '');

  return /already been completed/i.test(msg);
};

/**
 * Report an error thrown by a request-flow submit/upload to Sentry.
 *
 * ChiroUpAPI / Safari16API reject with a plain object
 * `{ response: { data: { statusCode, message, ... } } }` rather than an
 * `Error`. Handing that straight to `Sentry.captureException` produces the
 * useless "Object captured as exception with keys: response" grouping with
 * no status visible (HEALTHCOM-SCHEDULE-1V). This normalizes it into a real
 * `Error` so Sentry groups by status + message, and:
 *   - skips transient network failures (expected, self-healing noise), and
 *   - attaches only the statusCode (never the raw response body, which can
 *     echo submitted PHI) plus any caller-supplied context.
 */
export const reportRequestSubmitError = (err: any, context?: Record<string, unknown>) => {
  if (isTransientNetworkError(err)) {
    return;
  }

  if (isAlreadyCompletedError(err)) {
    return;
  }

  // Already a real Error (or an explicitly-wrapped one) — report as-is.
  if (err instanceof Error) {
    Sentry.captureException(err, context ? { extra: context } : undefined);

    return;
  }

  const data = err?.response?.data;
  const statusCode = data?.statusCode ?? err?.status;
  // Server error messages ("Unable to retrieve request.", "Failed to save
  // information.") are safe to surface; the raw `data` object is not (it
  // can contain submitted patient fields), so it is deliberately omitted.
  const message =
    (typeof data?.message === 'string' && data.message) ||
    (typeof err?.message === 'string' && err.message) ||
    'Request submit failed';

  const normalized = new Error(
    statusCode
      ? `Request submit failed (${statusCode}): ${message}`
      : `Request submit failed: ${message}`
  );
  normalized.name = 'RequestSubmitError';

  Sentry.captureException(normalized, {
    extra: { ...context, statusCode },
  });
};
